"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Pagination, PaginationContent, PaginationItem, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
function BlogFeedConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage
  } = usePosts();
  if (loading) {
    return <div data-section-id="115587" className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4">
            <div className="w-full aspect-video bg-muted/30 rounded-xl animate-pulse" />
            <div className="h-6 w-3/4 bg-muted/30 rounded animate-pulse" />
            <div className="h-4 w-1/2 bg-muted/30 rounded animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-destructive mb-4 font-default">Failed to load investment insights.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground font-default">No insights published yet. Check back soon for expert investment guidance.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post, i) => <div key={post.id} data-index={i}>
            <Link to={post.link || "#"} className="block h-full group outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
              <Card className="h-full border-none shadow-none bg-transparent p-0 gap-4 transition-transform duration-500 ease-out group-hover:-translate-y-2">
                <AspectRatio ratio={16 / 9} className="overflow-hidden rounded-xl bg-muted shadow-lg">
                  <img src={post.featuredImage || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1628823/img/blog_placeholder_defectwise.png"} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 group-hover:scale-110" />
                </AspectRatio>
                <CardContent className="p-0 pt-6">
                  <div className="font-default text-xs uppercase tracking-widest text-primary font-bold mb-3 flex items-center gap-2">
                    {post.categories && post.categories.length > 0 && <span>{post.categories[0].name}</span>}
                    {post.date && <>
                        <span aria-hidden="true" className="opacity-30">•</span>
                        <time dateTime={post.date} className="text-muted-foreground font-medium normal-case tracking-normal">
                          {new Date(post.date).toLocaleDateString("en-GB", {
                      month: "short",
                      year: "numeric"
                    })}
                        </time>
                      </>}
                  </div>
                  <h3 className="font-default text-xl md:text-2xl font-bold text-secondary mb-4 leading-tight group-hover:text-primary transition-colors duration-300 line-clamp-3">
                    {post.title}
                  </h3>
                  <div className="font-default text-sm text-primary font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Read Analysis <span className="text-lg">→</span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>)}
      </div>

      {(hasNext || hasPrev) && <Pagination className="mt-12">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious onClick={prevPage} className={!hasPrev ? "pointer-events-none opacity-30" : "cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"} aria-disabled={!hasPrev} />
            </PaginationItem>
            <PaginationItem>
              <PaginationNext onClick={nextPage} className={!hasNext ? "pointer-events-none opacity-30" : "cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"} aria-disabled={!hasNext} />
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function LatestPropertyAdvice() {
  const wp_query_params = {
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="the-investors-guide" className="py-16 md:py-24 lg:py-32 bg-background text-foreground">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 md:mb-16 max-w-3xl">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-secondary mb-6 tracking-tight">
            The Investor's Guide
          </h2>
          <p className="text-lg text-muted-foreground font-default leading-relaxed">
            Strategic insights on high-yield UK social housing, property market trends, and ESG-led wealth creation from our senior investment analysts.
          </p>
        </div>

        <PostsProvider wp_query={{
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <BlogFeedConsumer />
        </PostsProvider>
      </div>
    </section>;
}